import depositService from "@/services/depositService";
import withdrawalService from "@/services/withdrawalService";
import dashboardService from "@/services/dashboardService";
import type {
  Deposit,
  Withdrawal,
  Operation,
  OperationListParams,
  OperationNote,
  ConfirmOperationRequest,
  AdminProcessRequest,
  CreateNoteRequest,
  DashboardStats,
} from "@/models/operation.model";
import type { Pagination } from "@/models/user.model";

type State = {
  loading: boolean;
  availableDeposits: Deposit[];
  availableWithdrawals: Withdrawal[];
  availableDepositsPagination: Pagination | null;
  availableWithdrawalsPagination: Pagination | null;
  mineDeposits: Deposit[];
  mineWithdrawals: Withdrawal[];
  mineDepositsPagination: Pagination | null;
  mineWithdrawalsPagination: Pagination | null;
  currentOperation: Operation | null;
  notes: OperationNote[];
  availableListParams: OperationListParams;
  mineListParams: OperationListParams;
  dashboardStats: DashboardStats | null;
  dashboardLoading: boolean;
};

const createInitialState = (): State => ({
  loading: false,
  availableDeposits: [],
  availableWithdrawals: [],
  availableDepositsPagination: null,
  availableWithdrawalsPagination: null,
  mineDeposits: [],
  mineWithdrawals: [],
  mineDepositsPagination: null,
  mineWithdrawalsPagination: null,
  currentOperation: null,
  notes: [],
  availableListParams: { page: 1, pageSize: 10 },
  mineListParams: { page: 1, pageSize: 10 },
  dashboardStats: null,
  dashboardLoading: false,
});

const state: State = createInitialState();

const mutations = {
  setLoading(state: State, loading: boolean) {
    state.loading = loading;
  },
  setAvailableDeposits(
    state: State,
    { items, pagination }: { items: Deposit[]; pagination: Pagination }
  ) {
    state.availableDeposits = items;
    state.availableDepositsPagination = pagination;
  },
  setAvailableWithdrawals(
    state: State,
    { items, pagination }: { items: Withdrawal[]; pagination: Pagination }
  ) {
    state.availableWithdrawals = items;
    state.availableWithdrawalsPagination = pagination;
  },
  setMineDeposits(
    state: State,
    { items, pagination }: { items: Deposit[]; pagination: Pagination }
  ) {
    state.mineDeposits = items;
    state.mineDepositsPagination = pagination;
  },
  setMineWithdrawals(
    state: State,
    { items, pagination }: { items: Withdrawal[]; pagination: Pagination }
  ) {
    state.mineWithdrawals = items;
    state.mineWithdrawalsPagination = pagination;
  },
  setCurrentOperation(state: State, op: Operation | null) {
    state.currentOperation = op;
  },
  setNotes(state: State, notes: OperationNote[]) {
    state.notes = notes;
  },
  addNote(state: State, note: OperationNote) {
    state.notes.push(note);
  },
  setAvailableListParams(state: State, params: Partial<OperationListParams>) {
    state.availableListParams = { ...state.availableListParams, ...params };
  },
  setMineListParams(state: State, params: Partial<OperationListParams>) {
    state.mineListParams = { ...state.mineListParams, ...params };
  },
  removeAvailableDeposit(state: State, id: string) {
    state.availableDeposits = state.availableDeposits.filter((d) => d.id !== id);
  },
  removeAvailableWithdrawal(state: State, id: string) {
    state.availableWithdrawals = state.availableWithdrawals.filter(
      (w) => w.id !== id
    );
  },
  setDashboardStats(state: State, stats: DashboardStats | null) {
    state.dashboardStats = stats;
  },
  setDashboardLoading(state: State, loading: boolean) {
    state.dashboardLoading = loading;
  },
  resetState(state: State) {
    Object.assign(state, createInitialState());
  },
};

const getters = {
  loading: (state: State) => state.loading,
  availableDeposits: (state: State) => state.availableDeposits,
  availableWithdrawals: (state: State) => state.availableWithdrawals,
  availableDepositsPagination: (state: State) => state.availableDepositsPagination,
  availableWithdrawalsPagination: (state: State) =>
    state.availableWithdrawalsPagination,
  mineDeposits: (state: State) => state.mineDeposits,
  mineWithdrawals: (state: State) => state.mineWithdrawals,
  mineDepositsPagination: (state: State) => state.mineDepositsPagination,
  mineWithdrawalsPagination: (state: State) => state.mineWithdrawalsPagination,
  currentOperation: (state: State) => state.currentOperation,
  notes: (state: State) => state.notes,
  availableListParams: (state: State) => state.availableListParams,
  mineListParams: (state: State) => state.mineListParams,
  dashboardStats: (state: State) => state.dashboardStats,
  dashboardLoading: (state: State) => state.dashboardLoading,
};

const actions = {
  async fetchAvailableDeposits({
    commit,
    state,
    dispatch,
  }: {
    commit: any;
    state: State;
    dispatch: any;
  }) {
    commit("setLoading", true);
    commit("setAvailableDeposits", { items: [], pagination: state.availableDepositsPagination });
    try {
      const res = await depositService.getAvailable(state.availableListParams);
      commit("setAvailableDeposits", res);
    } catch (error) {
      await dispatch("notificator/apiError", error, { root: true });
    } finally {
      commit("setLoading", false);
    }
  },

  async fetchAvailableWithdrawals({
    commit,
    state,
    dispatch,
  }: {
    commit: any;
    state: State;
    dispatch: any;
  }) {
    commit("setLoading", true);
    commit("setAvailableWithdrawals", { items: [], pagination: state.availableWithdrawalsPagination });
    try {
      const res = await withdrawalService.getAvailable(state.availableListParams);
      commit("setAvailableWithdrawals", res);
    } catch (error) {
      await dispatch("notificator/apiError", error, { root: true });
    } finally {
      commit("setLoading", false);
    }
  },

  async fetchMineDeposits({
    commit,
    state,
    dispatch,
  }: {
    commit: any;
    state: State;
    dispatch: any;
  }) {
    commit("setLoading", true);
    commit("setMineDeposits", { items: [], pagination: state.mineDepositsPagination });
    try {
      const res = await depositService.getMine(state.mineListParams);
      commit("setMineDeposits", res);
    } catch (error) {
      await dispatch("notificator/apiError", error, { root: true });
    } finally {
      commit("setLoading", false);
    }
  },

  async fetchMineWithdrawals({
    commit,
    state,
    dispatch,
  }: {
    commit: any;
    state: State;
    dispatch: any;
  }) {
    commit("setLoading", true);
    commit("setMineWithdrawals", { items:[], pagination: state.mineWithdrawalsPagination });
    try {
      const res = await withdrawalService.getMine(state.mineListParams);
      commit("setMineWithdrawals", res);
    } catch (error) {
      await dispatch("notificator/apiError", error, { root: true });
    } finally {
      commit("setLoading", false);
    }
  },

  async takeDeposit(
    { commit, dispatch }: { commit: any; dispatch: any },
    depositId: string
  ) {
    try {
      const deposit = await depositService.take(depositId);
      commit("removeAvailableDeposit", depositId);
      await dispatch("notificator/success", "operation-taken", { root: true });
      return deposit;
    } catch (error: any) {
      if (error?.errorCode === "OPERATION_ALREADY_TAKEN") {
        commit("removeAvailableDeposit", depositId);
      }
      await dispatch("notificator/apiError", error, { root: true });
      throw error;
    }
  },

  async takeWithdrawal(
    { commit, dispatch }: { commit: any; dispatch: any },
    withdrawalId: string
  ) {
    try {
      const withdrawal = await withdrawalService.take(withdrawalId);
      commit("removeAvailableWithdrawal", withdrawalId);
      await dispatch("notificator/success", "operation-taken", { root: true });
      return withdrawal;
    } catch (error: any) {
      if (error?.errorCode === "OPERATION_ALREADY_TAKEN") {
        commit("removeAvailableWithdrawal", withdrawalId);
      }
      await dispatch("notificator/apiError", error, { root: true });
      throw error;
    }
  },

  async confirmOperation(
    { dispatch }: { dispatch: any },
    {
      id,
      operationType,
      data,
    }: {
      id: string;
      operationType: "Deposit" | "Withdrawal";
      data: ConfirmOperationRequest;
    }
  ) {
    try {
      if (operationType === "Deposit") {
        await depositService.confirm(id, data);
      } else {
        await withdrawalService.confirm(id, data);
      }
      const key = data.approve ? "operation-confirmed" : "operation-rejected";
      await dispatch("notificator/success", key, { root: true });
    } catch (error) {
      await dispatch("notificator/apiError", error, { root: true });
      throw error;
    }
  },

  async fetchNotes(
    { commit, dispatch }: { commit: any; dispatch: any },
    { id, operationType }: { id: string; operationType: "Deposit" | "Withdrawal" }
  ) {
    try {
      const notes =
        operationType === "Deposit"
          ? await depositService.getNotes(id)
          : await withdrawalService.getNotes(id);
      commit("setNotes", notes);
    } catch (error) {
      await dispatch("notificator/apiError", error, { root: true });
    }
  },

  async createNote(
    { commit, dispatch }: { commit: any; dispatch: any },
    {
      id,
      operationType,
      content,
      image,
    }: {
      id: string;
      operationType: "Deposit" | "Withdrawal";
      content?: string;
      image?: File | null;
    }
  ) {
    try {
      const payload: CreateNoteRequest = {
        content,
        image: image ?? null,
      };
      const note =
        operationType === "Deposit"
          ? await depositService.createNote(id, payload)
          : await withdrawalService.createNote(id, payload);
      commit("addNote", note);
      return note;
    } catch (error) {
      await dispatch("notificator/apiError", error, { root: true });
      throw error;
    }
  },

  async markNotesViewed(
    { dispatch }: { dispatch: any },
    { id, operationType }: { id: string; operationType: "Deposit" | "Withdrawal" }
  ) {
    try {
      if (operationType === "Deposit") {
        await depositService.markAllNotesViewed(id);
      } else {
        await withdrawalService.markAllNotesViewed(id);
      }
    } catch (error) {
      await dispatch("notificator/apiError", error, { root: true });
    }
  },

  updateAvailableListParams(
    { commit, dispatch }: { commit: any; dispatch: any },
    params: Partial<OperationListParams> & { tab: "deposits" | "withdrawals" }
  ) {
    const { tab, ...listParams } = params;
    commit("setAvailableListParams", listParams);
    return tab === "deposits"
      ? dispatch("fetchAvailableDeposits")
      : dispatch("fetchAvailableWithdrawals");
  },

  updateMineListParams(
    { commit, dispatch }: { commit: any; dispatch: any },
    params: Partial<OperationListParams> & { tab: "deposits" | "withdrawals" }
  ) {
    const { tab, ...listParams } = params;
    commit("setMineListParams", listParams);
    return tab === "deposits"
      ? dispatch("fetchMineDeposits")
      : dispatch("fetchMineWithdrawals");
  },

  async fetchDashboard(
    { commit, dispatch }: { commit: any; dispatch: any },
    { role }: { role: "Admin" | "Provider" }
  ) {
    commit("setDashboardStats", null);
    commit("setDashboardLoading", true);
    try {
      const stats =
        role === "Admin"
          ? await dashboardService.getAdminDashboard()
          : await dashboardService.getProviderDashboard();
      commit("setDashboardStats", stats);
    } catch (error) {
      await dispatch("notificator/apiError", error, { root: true });
    } finally {
      commit("setDashboardLoading", false);
    }
  },

  async releaseOperation(
    { dispatch }: { dispatch: any },
    { id, operationType }: { id: string; operationType: "Deposit" | "Withdrawal" }
  ) {
    try {
      if (operationType === "Deposit") {
        await depositService.release(id);
      } else {
        await withdrawalService.release(id);
      }
      await dispatch("notificator/success", "operation-released", { root: true });
    } catch (error) {
      await dispatch("notificator/apiError", error, { root: true });
      throw error;
    }
  },

  async adminReleaseOperation(
    { dispatch }: { dispatch: any },
    { id, operationType }: { id: string; operationType: "Deposit" | "Withdrawal" }
  ) {
    try {
      if (operationType === "Deposit") {
        await depositService.adminRelease(id);
      } else {
        await withdrawalService.adminRelease(id);
      }
      await dispatch("notificator/success", "operation-released", { root: true });
    } catch (error) {
      await dispatch("notificator/apiError", error, { root: true });
      throw error;
    }
  },

  async adminProcessOperation(
    { dispatch }: { dispatch: any },
    {
      id,
      operationType,
      data,
    }: {
      id: string;
      operationType: "Deposit" | "Withdrawal";
      data: AdminProcessRequest;
    }
  ) {
    try {
      if (operationType === "Deposit") {
        await depositService.adminProcess(id, data);
      } else {
        await withdrawalService.adminProcess(id, data);
      }
      const key = data.approve ? "operation-confirmed" : "operation-rejected";
      await dispatch("notificator/success", key, { root: true });
    } catch (error) {
      await dispatch("notificator/apiError", error, { root: true });
      throw error;
    }
  },
};

export default { namespaced: true, state, mutations, getters, actions };
